import type { RequestInitiator } from '@ms/yammer-telemetry-support';
import { acquireToken } from '@ms/yammer-web-authenticators';

import type { ManagedApiCaching, ManagedApiLifecycle, ManagedApiRequest } from '../..';
import { managedApiAction } from '../..';
import { reportEcsEtagMapping } from '../../experiments';
import type { EcsPayload, NormalizedEcsEntities } from '../../normalization/ecs/normalizeEcsPayload';
import { normalizeEcsPayload } from '../../normalization/ecs/normalizeEcsPayload';
import type { PromiseThunkAction } from '../../types';
import { doPersistedFeatureRolloutsExist } from '../featureRollouts/selectors';
import { getAdalResourceForYammer, getEcsHost } from '../session/selectors';

import type {
  LoadEcsTreatmentsFulfilledAction,
  LoadEcsTreatmentsPendingAction,
  LoadEcsTreatmentsRejectedAction,
} from './actions.types';
import { getCacheKey } from './cacheKey';
import { apiName } from './constants';
import type { EcsUserContext } from './ecsQuery';
import { getEcsRequestQueryFromUserContext, getManagedApiRequestQuery } from './ecsQuery';

const loadEcsTreatmentsPending = (): LoadEcsTreatmentsPendingAction => ({
  type: 'FEATURE_FLIGHTING.LOAD_ECS_TREATMENTS_PENDING',
});

type LoadEcsTreatmentsFulfilled = (normalizedEcsEntities: NormalizedEcsEntities) => LoadEcsTreatmentsFulfilledAction;
const loadEcsTreatmentsFulfilled: LoadEcsTreatmentsFulfilled = (normalizedEcsEntities) => ({
  type: 'FEATURE_FLIGHTING.LOAD_ECS_TREATMENTS_FULFILLED',
  payload: normalizedEcsEntities,
});

const loadEcsTreatmentsRejected = (error: unknown): LoadEcsTreatmentsRejectedAction => ({
  type: 'FEATURE_FLIGHTING.LOAD_ECS_TREATMENTS_REJECTED',
  payload: { error },
  error: true,
});

export interface LoadEcsTreatmentsOptions {
  readonly caching?: ManagedApiCaching;
  readonly persistKey?: string;
  readonly initiator?: RequestInitiator;
  readonly userContext?: EcsUserContext;
}

export type LoadEcsTreatments = (options?: LoadEcsTreatmentsOptions) => PromiseThunkAction<void>;
export const loadEcsTreatments: LoadEcsTreatments = (options) => async (dispatch, getState) => {
  const state = getState();
  const ecsHost = getEcsHost(state);
  const userContext = options?.userContext;
  let query;
  if (userContext) {
    query = getEcsRequestQueryFromUserContext(userContext);
  } else {
    const yammerResource = getAdalResourceForYammer(state);
    const token = await acquireToken({ scopes: [yammerResource] });
    query = getManagedApiRequestQuery(token);
  }

  const request: ManagedApiRequest = {
    method: 'GET',
    host: ecsHost,
    path: '/config/v1/YammerClients/0.0.1',
    apiName,
    requestType: 'Ecs',
    initiator: options?.initiator,
    query,
    skipTokenCheck: true,
  };

  const processEcsPayloadAndReportToTelemetry = (payload: EcsPayload) => {
    const normalizedEcsEntities = normalizeEcsPayload(payload);
    reportEcsEtagMapping(normalizedEcsEntities.ecsConfig.etag, normalizedEcsEntities.ecsConfig.configIds);

    return loadEcsTreatmentsFulfilled(normalizedEcsEntities);
  };

  const lifecycle: ManagedApiLifecycle<EcsPayload> = {
    pending: loadEcsTreatmentsPending,
    fulfilled: processEcsPayloadAndReportToTelemetry,
    rejected: loadEcsTreatmentsRejected,
  };

  const caching = options && options.caching;
  const cachingWithKey = caching && {
    ...caching,
    key: getCacheKey(query),
  };

  const persistKey = options && options.persistKey;
  const telemetry =
    (persistKey && {
      actionName: 'initializeEcsFeatureFlags' as const,
      cacheExists: await doPersistedFeatureRolloutsExist(persistKey),
    }) ||
    undefined;

  await dispatch(managedApiAction({ request, lifecycle, caching: cachingWithKey, telemetry }));
};
